import { useState, useEffect, useMemo } from 'react'
import { parentApi, type ParentChild } from '@/lib/parent-api'
import { useDeferredLoading } from '@/hooks/useDeferredLoading'
import type { CalendarEvent, ParentEventType, CreateAbsenceRequest } from '@/types/calendar'
import { getEventTypeLabel, getEventTypeDotColor, PARENT_EVENT_TYPES, isParentEventType } from '@/types/calendar'

// Helper functions
function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

function formatDateRange(startDate: string, endDate: string): string {
  if (startDate === endDate) {
    return formatDate(startDate)
  }
  return `${formatDate(startDate)} - ${formatDate(endDate)}`
}

function getMonthDays(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const days: Date[] = []

  // Add days from previous month to fill first week
  const startDayOfWeek = firstDay.getDay() || 7 // Convert Sunday (0) to 7
  for (let i = startDayOfWeek - 1; i > 0; i--) {
    days.push(new Date(year, month, 1 - i))
  }

  // Add days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i))
  }

  // Add days from next month to fill last week
  const remainingDays = 7 - (days.length % 7)
  if (remainingDays < 7) {
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i))
    }
  }

  return days
}

function dateToString(date: Date): string {
  return date.toISOString().split('T')[0]
}

// Event Card Component
function EventCard({ event, onEdit, onDelete }: {
  event: CalendarEvent
  onEdit: () => void
  onDelete: () => void
}) {
  const [showActions, setShowActions] = useState(false)
  const canEdit = event.createdByRole === 'parent'

  return (
    <div className="bg-white rounded-lg border p-3 mb-2">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-2 h-2 rounded-full ${getEventTypeDotColor(event.type)}`}></div>
            <span className="text-sm font-medium text-gray-900 ph-no-capture">{event.title}</span>
            <span className="text-xs text-gray-500">({getEventTypeLabel(event.type)})</span>
          </div>
          <div className="text-xs text-gray-500 mb-1">
            {formatDateRange(event.startDate, event.endDate)}
          </div>
          {event.children.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {event.children.map(child => (
                <span key={child.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 ph-no-capture">
                  {child.firstName}
                </span>
              ))}
            </div>
          )}
          {event.notes && (
            <p className="text-xs text-gray-600 mt-1 ph-no-capture">{event.notes}</p>
          )}
          {!canEdit && (
            <span className="text-xs text-gray-400 mt-1 block">Added by childminder</span>
          )}
        </div>
        {canEdit && (
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
            {showActions && (
              <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border py-1 z-10 min-w-[100px]">
                <button
                  onClick={() => { setShowActions(false); onEdit() }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  Edit
                </button>
                <button
                  onClick={() => { setShowActions(false); onDelete() }}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-50"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Add Absence Modal
function AddAbsenceModal({
  isOpen,
  onClose,
  onSave,
  children,
  editEvent,
  selectedDate
}: {
  isOpen: boolean
  onClose: () => void
  onSave: (data: CreateAbsenceRequest) => Promise<void>
  children: ParentChild[]
  editEvent?: CalendarEvent | null
  selectedDate?: string
}) {
  const [type, setType] = useState<ParentEventType>('holiday')
  const [notes, setNotes] = useState('')
  const [startDate, setStartDate] = useState(selectedDate || dateToString(new Date()))
  const [endDate, setEndDate] = useState(selectedDate || dateToString(new Date()))
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (editEvent && isParentEventType(editEvent.type)) {
      setType(editEvent.type)
      setNotes(editEvent.notes || '')
      setStartDate(editEvent.startDate)
      setEndDate(editEvent.endDate)
      setSelectedChildIds(editEvent.children.map(c => c.id))
    } else {
      setType('holiday')
      setNotes('')
      setStartDate(selectedDate || dateToString(new Date()))
      setEndDate(selectedDate || dateToString(new Date()))
      // Default to first child if only one
      setSelectedChildIds(children.length === 1 ? [children[0].id] : [])
    }
    setError(null)
  }, [editEvent, selectedDate, isOpen, children])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedChildIds.length === 0) {
      setError('Select at least one child')
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      await onSave({
        type,
        startDate,
        endDate,
        childIds: selectedChildIds,
        notes: notes || undefined,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save absence')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50">
      <div className="bg-white w-full rounded-t-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{editEvent ? 'Edit Absence' : 'Report Absence'}</h2>
          <button onClick={onClose} className="p-2 text-gray-500">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
          )}

          {/* Absence Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
            <div className="grid grid-cols-2 gap-2">
              {PARENT_EVENT_TYPES.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium border flex items-center justify-center gap-2 ${
                    type === t
                      ? t === 'holiday' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-red-50 border-red-500 text-red-700'
                      : 'bg-white border-gray-200 text-gray-700'
                  }`}
                >
                  {t === 'holiday' ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  )}
                  {getEventTypeLabel(t)}
                </button>
              ))}
            </div>
          </div>

          {/* Children Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Which child?</label>
            <div className="space-y-2">
              {children.map(child => (
                <label key={child.id} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedChildIds.includes(child.id)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedChildIds([...selectedChildIds, child.id])
                      } else {
                        setSelectedChildIds(selectedChildIds.filter(id => id !== child.id))
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 ph-no-capture">{child.firstName} {child.lastName}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                min={startDate}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={type === 'holiday' ? 'Going to the beach!' : 'Any symptoms or details...'}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : editEvent ? 'Update Absence' : 'Report Absence'}
          </button>
        </form>
      </div>
    </div>
  )
}

export function ParentCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [children, setChildren] = useState<ParentChild[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const showLoading = useDeferredLoading(isLoading)
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editEvent, setEditEvent] = useState<CalendarEvent | null>(null)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const monthDays = useMemo(() => getMonthDays(year, month), [year, month])

  const monthName = currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  // Get events for current month
  const loadData = async () => {
    try {
      setIsLoading(true)
      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)

      const [eventsData, childrenData] = await Promise.all([
        parentApi.getCalendarEvents({
          startDate: dateToString(firstDay),
          endDate: dateToString(lastDay),
        }),
        parentApi.getChildren(),
      ])

      setEvents(eventsData.events)
      setChildren(childrenData.children)
    } catch {
      // Failed to load calendar data
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [year, month])

  // Get events for a specific date
  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const dateStr = dateToString(date)
    return events.filter(event => {
      return dateStr >= event.startDate && dateStr <= event.endDate
    })
  }

  // Navigate months
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1))
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1))
  const goToToday = () => setCurrentDate(new Date())

  // Handle event actions
  const handleSaveAbsence = async (data: CreateAbsenceRequest) => {
    if (editEvent) {
      await parentApi.updateAbsence(editEvent.id, {
        startDate: data.startDate,
        endDate: data.endDate,
        notes: data.notes,
      })
    } else {
      await parentApi.createAbsence(data)
    }
    await loadData()
    setEditEvent(null)
  }

  const handleDeleteEvent = async (eventId: string) => {
    if (confirm('Delete this absence?')) {
      await parentApi.deleteAbsence(eventId)
      await loadData()
    }
  }

  const handleDateClick = (date: Date) => {
    setSelectedDate(dateToString(date))
  }

  const today = dateToString(new Date())

  // List view: group events by date
  const groupedEvents = useMemo(() => {
    const groups = new Map<string, CalendarEvent[]>()
    events.forEach(event => {
      const startDate = event.startDate
      if (!groups.has(startDate)) {
        groups.set(startDate, [])
      }
      groups.get(startDate)!.push(event)
    })
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [events])

  if (showLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  // Still loading but not showing skeleton yet - render nothing to prevent flash
  if (isLoading) {
    return null
  }

  return (
    <div className="p-4 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')}
            className="p-2 rounded-lg border border-gray-200 text-gray-600"
          >
            {viewMode === 'calendar' ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </button>
          <button
            onClick={() => { setEditEvent(null); setShowAddModal(true) }}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            Report Absence
          </button>
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <>
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900">{monthName}</h2>
              <button onClick={goToToday} className="text-xs text-primary-600">Today</button>
            </div>
            <button onClick={nextMonth} className="p-2 text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white rounded-xl border overflow-hidden">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="py-2 text-center text-xs font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Day Cells */}
            <div className="grid grid-cols-7">
              {monthDays.map((date, index) => {
                const dateStr = dateToString(date)
                const isCurrentMonth = date.getMonth() === month
                const isToday = dateStr === today
                const isSelected = dateStr === selectedDate
                const dayEvents = getEventsForDate(date)

                return (
                  <button
                    key={index}
                    onClick={() => handleDateClick(date)}
                    className={`min-h-[60px] p-1 border-b border-r text-left relative ${
                      !isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                    } ${isSelected ? 'bg-primary-50' : ''}`}
                  >
                    <span className={`text-sm ${isToday ? 'bg-primary-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : ''}`}>
                      {date.getDate()}
                    </span>
                    {dayEvents.length > 0 && (
                      <div className="flex gap-0.5 mt-1 flex-wrap">
                        {dayEvents.slice(0, 3).map(event => (
                          <div
                            key={event.id}
                            className={`w-1.5 h-1.5 rounded-full ${getEventTypeDotColor(event.type)}`}
                          />
                        ))}
                        {dayEvents.length > 3 && (
                          <span className="text-[10px] text-gray-500">+{dayEvents.length - 3}</span>
                        )}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Selected Date Events */}
          {selectedDate && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{formatDate(selectedDate)}</h3>
                <button
                  onClick={() => { setShowAddModal(true) }}
                  className="text-sm text-primary-600 font-medium"
                >
                  + Report Absence
                </button>
              </div>
              {getEventsForDate(new Date(selectedDate + 'T00:00:00')).length > 0 ? (
                getEventsForDate(new Date(selectedDate + 'T00:00:00')).map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onEdit={() => { setEditEvent(event); setShowAddModal(true) }}
                    onDelete={() => handleDeleteEvent(event.id)}
                  />
                ))
              ) : (
                <p className="text-sm text-gray-500">No events on this day</p>
              )}
            </div>
          )}
        </>
      ) : (
        /* List View */
        <div className="space-y-4">
          {groupedEvents.length > 0 ? (
            groupedEvents.map(([date, dateEvents]) => (
              <div key={date}>
                <h3 className="font-semibold text-gray-900 mb-2">{formatDate(date)}</h3>
                {dateEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onEdit={() => { setEditEvent(event); setShowAddModal(true) }}
                    onDelete={() => handleDeleteEvent(event.id)}
                  />
                ))}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-gray-500 mb-2">No events this month</p>
              <p className="text-sm text-gray-400">Report a holiday or sick day to get started</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AddAbsenceModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditEvent(null) }}
        onSave={handleSaveAbsence}
        children={children}
        editEvent={editEvent}
        selectedDate={selectedDate || undefined}
      />
    </div>
  )
}
